import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Tag, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Sparkles, 
  Search, 
  CheckCircle2, 
  TrendingUp, 
  Plus 
} from 'lucide-react';

export const YoutubeTagGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [keyword, setKeyword] = useState('react web development');
  const [activeTags, setActiveTags] = useState([
    'react web development', 'react js tutorial', 'web development for beginners', 
    'learn react 2026', 'frontend development', 'javascript frameworks', 'react project tutorial',
    'web dev roadmap', 'react tips and tricks', 'how to learn react fast', 'css grid tutorial',
    'full stack developer', 'react component lifecycle', 'vite react setup', 'modern web tech'
  ]);
  const [copied, setCopied] = useState(false);

  const tagPool = {
    'react web development': [
      'react web development', 'react js tutorial', 'web development for beginners', 
      'learn react 2026', 'frontend development', 'javascript frameworks', 'react project tutorial',
      'web dev roadmap', 'react tips and tricks', 'how to learn react fast', 'css grid tutorial',
      'full stack developer', 'react component lifecycle', 'vite react setup', 'modern web tech'
    ],
    'crypto trading': [
      'crypto trading strategies', 'bitcoin bull run', 'ethereum trading tutorial',
      'day trading crypto', 'how to buy bitcoin', 'crypto market analysis', 'altcoins to buy',
      'cryptocurrency news today', 'leverage trading tutorial', 'crypto investing 2026', 'web3 tokens'
    ],
    'gaming setup': [
      'gaming setup tour 2026', 'budget gaming pc setup', 'ultimate desk setup',
      'rgb room decor', 'mechanical gaming keyboard', 'wireless gaming mouse review',
      'dual monitor gaming setup', 'pc build tutorial', 'streaming setup for beginners'
    ],
    'fitness workout': [
      '15 min home workout', 'fat burn hiit session', 'no equipment abs workout',
      'build muscle at home', 'daily stretching routine', 'fitness motivation 2026',
      'healthy meal prep ideas', 'weight loss transformation', 'cardio vs weightlifting'
    ]
  };

  const generateTags = (e) => {
    if (e) e.preventDefault();
    const query = keyword.toLowerCase().trim();
    if (!query) return;

    // Look up or generate dynamic LSI cluster
    if (tagPool[query]) {
      setActiveTags(tagPool[query]);
    } else {
      const baseWords = query.split(' ');
      const synthesized = [
        query,
        `${query} tutorial`,
        `${query} for beginners`,
        `how to ${query}`,
        `best ${query} 2026`,
        `${query} tips and tricks`,
        `${query} review`,
        `${query} explained`,
        `learn ${query} fast`,
        `complete ${baseWords[0]} guide`,
        `advanced ${baseWords[0]} techniques`,
        `${query} full course`,
        `why ${query} matters`,
        `${query} secrets`,
        `${query} strategy`
      ];
      setActiveTags(synthesized);
    }
  };

  const toggleTag = (tag) => {
    if (activeTags.includes(tag)) {
      setActiveTags(activeTags.filter(t => t !== tag));
    } else {
      setActiveTags([...activeTags, tag]);
    }
  };

  const tagsString = activeTags.join(', ');
  const charCount = tagsString.length;

  const handleCopy = () => {
    navigator.clipboard.writeText(tagsString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free YouTube Tag Generator - AI Video Keywords" 
        description="Generate high-volume, trending YouTube tags and long-tail keywords. Boost video rankings and maximize reach with our viral tag engine."
        keywords="youtube tag generator, youtube keywords, video tags, viral youtube tags, youtube seo keywords, tag extractor"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>YouTube Tag Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          YouTube Tag & Keyword Generator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Generate highly optimized long-tail LSI search keywords to trigger the YouTube recommendation algorithm and boost video ranking.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs & Tag Cloud */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} style={{ color: '#ef4444' }} /> Enter Video Topic Keyword
            </h3>

            <form onSubmit={generateTags} style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input 
                  type="text" 
                  value={keyword} 
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="e.g., fitness workout, gaming setup, crypto trading" 
                  style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '1rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <button 
                type="submit" 
                style={{ background: '#ef4444', color: '#ffffff', border: 'none', padding: '0 24px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                Generate Tags
              </button>
            </form>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Try Popular Topics:</span>
              {['react web development', 'crypto trading', 'gaming setup', 'fitness workout'].map(preset => (
                <button 
                  key={preset}
                  type="button" 
                  onClick={() => { setKeyword(preset); setTimeout(() => setActiveTags(tagPool[preset]), 50); }}
                  style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                Generated Tags Pool ({activeTags.length})
              </h3>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Click tag to include / exclude</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {activeTags.map((tag, i) => (
                <button 
                  key={i}
                  type="button" 
                  onClick={() => toggleTag(tag)}
                  style={{ 
                    background: 'rgba(239, 68, 68, 0.08)', 
                    color: '#ef4444', 
                    border: '1px solid rgba(239, 68, 68, 0.2)', 
                    padding: '8px 14px', 
                    borderRadius: '10px', 
                    fontSize: '0.85rem', 
                    fontWeight: 600, 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Tag size={12} /> {tag}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Right Side: Output Box & Character Gauge */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp size={18} style={{ color: '#ef4444' }} /> Ready-to-Paste Tags
                </h3>
                <div style={{ fontSize: '0.8rem', color: charCount > 500 ? '#ef4444' : charCount > 400 ? '#10b981' : 'var(--text-secondary)', fontWeight: 700 }}>
                  Character Limit Gauge: {charCount} / 500 chars
                </div>
              </div>

              <AdToast message={toast} />
              <button 
                onClick={withAd('yt-tag-copy', handleCopy)}
                style={{ background: copied ? '#10b981' : '#ef4444', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied Tags!' : 'Copy Selected Tags'}
              </button>
            </div>

            <textarea 
              rows="6"
              readOnly 
              value={tagsString}
              style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.9rem', lineHeight: 1.6, background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }}
            />
          </div>

          <div style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(244, 63, 94, 0.05) 100%)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '20px', padding: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ background: '#ef4444', color: '#ffffff', padding: '10px', borderRadius: '12px' }}>
              <CheckCircle2 size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Algorithm Best Practice</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Paste these tags directly into your video upload settings box under &quot;Show More &gt; Tags&quot;. Place your exact primary focus keyword first, followed by broad category terms.
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="youtube-tag-generator" />
    </div>
  );
};

export default YoutubeTagGenerator;
